import React, { useState, useEffect } from 'react';
import { CModal, CModalHeader, CModalTitle, CModalBody } from '@coreui/react';
import Container from 'react-bootstrap/Container';
import { Form, Button } from 'react-bootstrap';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import Logo from '../assets/images/itc.webp';
import { clear, getObjectData } from '../services/Storage';
import jwt_decode from 'jwt-decode';
import swal from 'sweetalert';
import authService from '../services/Auth';
import { MutatingDots } from 'react-loader-spinner';
import { toast } from 'react-toastify';
import { validate, validateProperty } from '../actions/utils';
import ProfileModalSchema from '../schema/ProfileModalschema';
import { useNavigate } from 'react-router-dom';

function AppHeader() {
   const [selectedPage, setSelectedPage] = useState(window.location.pathname);
   const [user, setUser] = useState(null);
   const [viewModal, setViewModal] = useState(false);
   const [profile, setProfile] = useState({});
   const [isLoading, setIsLoading] = useState(false);
   const [profileErrors, setProfileErrors] = useState({});
   const [isPasswordLoading, setIsPasswordLoading] = useState(false);
   const navigate = useNavigate();

   useEffect(() => {
      const initializeUser = async () => {
         try {
            const tokenData = await getObjectData('auth');
            const userData = jwt_decode(tokenData.accessToken);
            setUser(userData);
         } catch {
            setUser(null);
         }
      };

      const fetchProfile = async () => {
         try {
            const response = await authService.profile();
            if (response.status === 200 && response.data) {
               setProfile(response.data);
            }
         } catch {
            toast.error('Failed to fetch profile.');
         }
      };

      initializeUser();
      fetchProfile();
   }, []);

   const handleProfileUpdate = async () => {
      setIsLoading(true);
      try {
         const response = await authService.UpdateProfile(profile);

         if (response.status === 200) {
            toast.success('Your profile updated successfully.', {
               position: 'top-right',
               autoClose: 1000,
            });
         } else if (response?.response?.status === 422) {
            toast.error('Email already exists.');
         } else {
            toast.error('Unable to update profile!');
         }
      } catch {
         toast.error('Unable to update.');
      } finally {
         setIsLoading(false);
      }
   };

   const handlePasswordUpdate = async () => {
      const passwordToValidate = { password: profile.password };
      const schema = { password: ProfileModalSchema.password };
   
      const errors = validate(passwordToValidate, schema);
   
      if (errors) {
         setProfileErrors({ ...profileErrors, ...errors });
         return;
      }
   
      setIsPasswordLoading(true);
      try {
         const response = await authService.setNewPassword({
            password: profile.password,
         });
   
         if (response.status === 201) {
            toast.success('Your password updated successfully.', {
               position: 'top-right',
               autoClose: 1000,
            });
            setProfile({ ...profile, password: '' }); // Optional: clear password after update
            setProfileErrors({ ...profileErrors, password: '' }); // Clear error
         } else if (
            response?.response?.status === 422 ||
            response?.response?.status === 400
         ) {
            const errors = response?.response?.data?.message || [
               'Password could not be updated',
            ];
            errors.forEach((error) =>
               toast.error(error, { autoClose: 10000, position: 'top-right' }),
            );
         } else {
            toast.error('Password could not be updated.');
         }
      } catch {
         toast.error('Something went wrong.');
      } finally {
         setIsPasswordLoading(false);
      }
   };
     

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      setProfile({ ...profile, [name]: value });

      const errorMessage = validateProperty(
         { name, value },
         ProfileModalSchema,
      );
      setProfileErrors({ ...profileErrors, [name]: errorMessage || '' });
   };

   const confirmLogout = () => {
      swal({
         icon: 'warning',
         title: 'Are you sure?',
         text: 'Log Out from this session',
         buttons: true,
      }).then(async (confirmed) => {
         if (confirmed) {
            await clear();
            navigate('/login');
            window.location.reload();
         }
      });
   };

   const renderNavbarBrand = () => {
      let href = '/';
      if (
         user?.roles.includes('Super Admin') ||
         user?.roles.includes('Station Admin')
      ) {
         href = '/dashboard';
      }
      return (
         <Navbar.Brand href={href} className="p-0">
            <img className="w-full h-20" src={Logo} alt="Logo" />
         </Navbar.Brand>
      );
   };

   return (
      <Navbar bg="white" expand="lg" className="p-0">
         <Container fluid="sm">
            {renderNavbarBrand()}
            <Navbar.Toggle aria-controls="navbarScroll" />
            <Navbar.Collapse id="navbarScroll">
               <Nav className="ml-auto">
                  {user?.roles.includes('Station Admin') && (
                     <Nav.Link
                        href="/services-list"
                        className="font-medium text-[#0168FF] text-md ml-4"
                        style={{
                           textDecoration:
                              selectedPage === '/services-list'
                                 ? 'underline'
                                 : 'none',
                        }}
                     >
                        {/* Services */}
                     </Nav.Link>
                  )}
                  {user?.roles.includes('Super Admin') && (
                     <>
                        <Nav.Link
                           href="https://cms.inspectthiscar.com/admin/auth/login"
                           target="_blank"
                           className="font-medium text-[#0168FF] text-md ml-4"
                        >
                           Write Blog
                        </Nav.Link>
                        <Nav.Link
                           href="/stations"
                           className="font-medium text-[#0168FF] text-md ml-4"
                           style={{
                              textDecoration:
                                 selectedPage === '/stations'
                                    ? 'underline'
                                    : 'none',
                           }}
                        >
                           Stations
                        </Nav.Link>
                     </>
                  )}
                  {user?.roles.includes('Station Admin') && (
                     <>
                        <Nav.Link
                           href="/dashboard"
                           className="font-medium text-[#0168FF] text-md ml-4"
                        >
                           Dashboard
                        </Nav.Link>
                        <Nav.Link
                           href="/schedule"
                           className="font-medium text-[#0168FF] text-md ml-4"
                        >
                           Schedule
                        </Nav.Link>
                        <Nav.Link
                           href="/addleads"
                           className="font-medium text-[#0168FF] text-md ml-4"
                        >
                           Service Requests
                        </Nav.Link>
                        <Nav.Link
                           href="/schedule-list"
                           className="font-medium text-[#0168FF] text-md ml-4"
                        >
                           Payment
                        </Nav.Link>
                     </>
                  )}
                  {/* <Nav.Link
                     href="/schedule"
                     className="font-medium text-[#0168FF] text-md ml-4"
                     style={{
                        textDecoration:
                           selectedPage === '/schedule' ? 'underline' : 'none',
                     }}
                  >
                     Appointment Schedule
                  </Nav.Link> */}
                  <Nav.Link
                     onClick={() => setViewModal(true)}
                     className="font-medium text-[#0168FF] text-md ml-4"
                  >
                     Profile
                  </Nav.Link>
                  <Nav.Link
                     onClick={confirmLogout}
                     className="font-medium text-[#0168FF] text-md ml-4"
                  >
                     Logout
                  </Nav.Link>
               </Nav>
            </Navbar.Collapse>
            {/* Profile Modal */}
            <CModal
               size="md"
               visible={viewModal}
               onClose={() => setViewModal(false)}
            >
               <CModalHeader onClose={() => setViewModal(false)}>
                  <CModalTitle className="font-semibold tracking-tight text-[#0A142F]">
                     Profile
                  </CModalTitle>
               </CModalHeader>
               <CModalBody>
                  <Form>
                     <Form.Group className="mb-3" controlId="profileName">
                        <Form.Label className="font-medium text-[#5A5A5D]">
                           Name
                        </Form.Label>
                        <Form.Control
                           className="rounded-md h-10 mb-2"
                           type="text"
                           placeholder="Name"
                           name="name"
                           value={profile.name}
                           onChange={handleInputChange}
                        />
                     </Form.Group>
                     <Form.Group className="mb-3" controlId="profileEmail">
                        <Form.Label className="font-medium text-[#5A5A5D]">
                           Email
                        </Form.Label>
                        <Form.Control
                           className="rounded-md h-10 mb-2"
                           type="email"
                           placeholder="Email"
                           name="email"
                           value={profile.email}
                           onChange={handleInputChange}
                        />
                     </Form.Group>
                     {isLoading ? (
                        <MutatingDots
                           height="80"
                           width="80"
                           radius="9"
                           color="black"
                           ariaLabel="loading"
                           wrapperStyle={{
                              display: 'flex',
                              justifyContent: 'center',
                           }}
                        />
                     ) : (
                        <Button
                           onClick={handleProfileUpdate}
                           className="bg-[#007DFA] w-full rounded-md border-none mb-4 font-medium h-10 text-md"
                        >
                           Update Profile
                        </Button>
                     )}
                  </Form>
                  <div className="text-center font-semibold text-[#0A142F] my-4">
                     OR
                  </div>
                  <Form>
                     <Form.Group className="mb-3" controlId="profilePassword">
                        <Form.Label className="font-medium text-[#5A5A5D]">
                           Password
                        </Form.Label>
                        <Form.Control
                           className="rounded-md h-10 mb-2"
                           type="password"
                           placeholder="Password"
                           name="password"
                           value={profile.password || ''}
                           onChange={handleInputChange}
                           />
                     </Form.Group>
                     {isPasswordLoading ? (
                        <MutatingDots
                           height="80"
                           width="80"
                           radius="9"
                           color="black"
                           ariaLabel="loading"
                           wrapperStyle={{
                              display: 'flex',
                              justifyContent: 'center',
                           }}
                        />
                     ) : (
                        <Button
                           onClick={handlePasswordUpdate}
                           className="bg-[#007DFA] w-full rounded-md border-none mb-4 font-medium h-10 text-md"
                        >
                           Update Password
                        </Button>
                     )}
                  </Form>
               </CModalBody>
            </CModal>
         </Container>
      </Navbar>
   );
}

export default AppHeader;
